import React, { useMemo } from 'react';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import MonthSettings from './MonthSettings';
import NegativeTermsForm from './NegativeTermsForm';
import LanguageForm from './LanguageForm';
import LocationForm from './LocationForm';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import VoxelAlert from '../../../components/voxel/Alert/VoxelAlert';

export interface DataSettingsProps {
  project: ProjectGq;
  setLanguageDirty: (dirty: boolean) => void;
  setLanguageSave: (saveFunction: () => Promise<unknown>) => void;
  setLocationDirty: (dirty: boolean) => void;
  setLocationSave: (saveFunction: () => Promise<unknown>) => void;
  setNegativeTermsDirty: (dirty: boolean) => void;
  setNegativeTermsSave: (saveFunction: () => Promise<unknown>) => void;
  reload: () => Promise<unknown>;
}

const DataSettings: React.FC<DataSettingsProps> = ({
  project,
  setLanguageDirty,
  setLanguageSave,
  setLocationDirty,
  setLocationSave,
  setNegativeTermsDirty,
  setNegativeTermsSave,
  reload,
}) => {
  // Loading results
  const { result: loadingResults } = projectsApiService.queries.getProjectStatus(
    { projectId: project.id },
    { fetchPolicy: 'cache-only' } // We handle the polling/loading in ProjectPage.tsx
  );

  const fetchingData = useMemo(
    () => loadingResults?.keywordSearchLoading || loadingResults?.rankingsLoading,
    [loadingResults]
  );

  return (
    <div className="flex flex-col gap-8">
      {fetchingData && (
        <VoxelAlert size="sm" iconSize="lg">
          These settings can't be edited while fetching keyword or rank data
        </VoxelAlert>
      )}
      <div>
        <VoxelText variant="title2">Data settings</VoxelText>
        <VoxelText variant="bread-normal" paddingTop>
          The following settings will be used to fetch keyword and rank data
        </VoxelText>
      </div>

      <MonthSettings project={project} reload={reload} disabled={fetchingData} />
      <NegativeTermsForm
        project={project}
        setDirty={setNegativeTermsDirty}
        setSave={setNegativeTermsSave}
        reload={reload}
        disabled={fetchingData}
      />
      <span id='location'>
        <LocationForm
          project={project}
          setDirty={setLocationDirty}
          setSave={setLocationSave}
          reload={reload}
          disabled={fetchingData}
        />
      </span>
      <span id='language'>
        <LanguageForm
          project={project}
          setDirty={setLanguageDirty}
          setSave={setLanguageSave}
          reload={reload}
          disabled={fetchingData}
        />
      </span>
    </div>
  );
};

export default DataSettings;
